import React, { useState, useEffect, useRef } from "react";
import { X } from "lucide-react";
import "./FeedbackModal.css";

const FeedbackModal = ({ isOpen, initialComment, onClose, onSubmit }) => {
  const [comment, setComment] = useState(initialComment || '');
  const textareaRef = useRef(null);
  const modalRef = useRef(null);
  
  // Update the comment state when initialComment changes
  useEffect(() => {
    setComment(initialComment || '');
  }, [initialComment, isOpen]);

  // Focus management and keyboard trap
  useEffect(() => {
    if (isOpen && textareaRef.current) {
      // Set focus to textarea when modal opens
      textareaRef.current.focus();
      
      // Save previously focused element to restore focus later
      const previouslyFocused = document.activeElement;
      
      // Handle Escape key to close modal
      const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
          onClose();
        }
      };
      
      document.addEventListener('keydown', handleKeyDown);
      
      // Cleanup function
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
        
        // Return focus to previously focused element when modal closes
        if (previouslyFocused) {
          previouslyFocused.focus();
        }
      };
    }
  }, [isOpen, onClose]);
  
  if (!isOpen) return null;
  
  return (
    <div 
      className="doug-feedback-modal-backdrop" 
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="feedback-modal-title"
    >
      <div 
        className="doug-feedback-modal" 
        onClick={e => e.stopPropagation()}
        ref={modalRef}
      >
        <div className="doug-feedback-modal-header">
          <h3 id="feedback-modal-title">Feedback</h3>
          <button 
            className="doug-feedback-close-btn" 
            onClick={onClose}
            aria-label="Close feedback form"
          >
            <X size={20} />
          </button>
        </div>
        <div className="doug-feedback-modal-body">
          <label htmlFor="feedback-comment" className="sr-only">Your feedback</label>
          <textarea 
            id="feedback-comment"
            ref={textareaRef}
            value={comment} 
            onChange={e => setComment(e.target.value)}
            placeholder="Please share your feedback..."
            rows={4}
            className="doug-feedback-textarea"
            aria-required="true"
          />
        </div>
        <div className="doug-feedback-modal-footer">
          <button 
            className="doug-feedback-cancel-btn" 
            onClick={onClose}
          >
            Cancel
          </button>
          <button 
            className="doug-feedback-submit-btn" 
            onClick={() => {
              onSubmit(comment);
              onClose();
            }}
          >
            Submit
          </button>
        </div>
      </div>
    </div>
  );
};

export default FeedbackModal;